import React, { useEffect, useState } from 'react';
import { LuRefreshCw, LuRotateCcw } from 'react-icons/lu';
import { Breadcrumbs, Select, StatusBadge } from '../../ui';
import { MAX_CUSTOM_DAYS, RANGE_OPTIONS, fmtRelative } from './format';

const todayIso = () => new Date().toISOString().slice(0, 10);

function daysBetween(from, to) {
  return Math.round((new Date(`${to}T00:00:00Z`) - new Date(`${from}T00:00:00Z`)) / 86400000) + 1;
}

function CustomRange({ from, to, onApply }) {
  const [draft, setDraft] = useState({ from: from || '', to: to || '' });
  useEffect(() => setDraft({ from: from || '', to: to || '' }), [from, to]);

  const today = todayIso();
  let problem = null;
  if (draft.from && draft.to) {
    if (draft.to < draft.from) problem = 'End date is before start date.';
    else if (daysBetween(draft.from, draft.to) > MAX_CUSTOM_DAYS) problem = `Range is limited to ${MAX_CUSTOM_DAYS} days.`;
  }
  const ready = draft.from && draft.to && !problem;

  return (
    <div className="pqd-custom-range">
      <label>
        <span className="pqd-sr-only">Start date (UTC)</span>
        <input
          type="date"
          className="pqd-date"
          value={draft.from}
          max={draft.to || today}
          onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))}
        />
      </label>
      <span aria-hidden="true">–</span>
      <label>
        <span className="pqd-sr-only">End date (UTC)</span>
        <input
          type="date"
          className="pqd-date"
          value={draft.to}
          min={draft.from || undefined}
          max={today}
          onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
        />
      </label>
      <button
        type="button"
        className="rt-btn rt-btn--secondary rt-btn--sm"
        disabled={!ready}
        onClick={() => onApply(draft.from, draft.to)}
      >
        Apply
      </button>
      {problem && <span className="pqd-field-error" role="alert">{problem}</span>}
    </div>
  );
}

export default function DashboardHeader({
  workspaceName,
  projectName,
  project,
  environments,
  filters,
  onFiltersChange,
  onReset,
  isDefaultFilters,
  onRefresh,
  refreshing,
  generatedAt,
  navigateTo,
}) {
  const name = project?.project_name || projectName || 'Project';
  const description = project?.description && project.description !== 'No description' ? project.description : null;
  const status = project?.status;

  return (
    <header className="pqd-header">
      <Breadcrumbs
        items={[
          { label: project?.workspace?.name || workspaceName || 'Workspace', path: 'workspace-home' },
          { label: name },
          { label: 'Dashboard' },
        ]}
        onNavigate={navigateTo}
      />

      <div className="pqd-header__row">
        <div className="pqd-header__identity">
          <div className="pqd-header__title-row">
            <h1 className="pqd-header__title">{name}</h1>
            {status && <StatusBadge status={status}>{String(status).replace(/_/g, ' ')}</StatusBadge>}
          </div>
          {description && <p className="pqd-header__desc">{description}</p>}
          <p className="pqd-header__meta">
            {generatedAt ? <>Updated {fmtRelative(generatedAt)}</> : 'Loading…'}
            {project?.last_result_at && <> · Last test result {fmtRelative(project.last_result_at)}</>}
          </p>
        </div>
        <button
          type="button"
          className="rt-btn rt-btn--secondary"
          onClick={onRefresh}
          disabled={refreshing}
          aria-label="Refresh dashboard"
        >
          <LuRefreshCw size={14} className={refreshing ? 'pqd-spin' : undefined} aria-hidden="true" />
          Refresh
        </button>
      </div>

      <div className="pqd-filters" role="group" aria-label="Dashboard filters">
        <div className="pqd-segmented" role="radiogroup" aria-label="Time range">
          {RANGE_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              type="button"
              role="radio"
              aria-checked={filters.range === opt.id}
              className={`pqd-segmented__item${filters.range === opt.id ? ' is-active' : ''}`}
              onClick={() => onFiltersChange({ range: opt.id })}
            >
              {opt.label}
            </button>
          ))}
        </div>

        {filters.range === 'custom' && (
          <CustomRange
            from={filters.from}
            to={filters.to}
            onApply={(from, to) => onFiltersChange({ range: 'custom', from, to })}
          />
        )}

        <div className="pqd-filters__env">
          <Select
            size="sm"
            value={filters.env || ''}
            onChange={(e) => onFiltersChange({ env: e.target.value || null })}
            aria-label="Environment"
            disabled={!environments?.length}
          >
            <option value="">All environments</option>
            {(environments || []).map((env) => (
              <option key={env.id} value={String(env.id)}>{env.name || `Environment ${env.id}`}</option>
            ))}
          </Select>
        </div>

        {!isDefaultFilters && (
          <button type="button" className="rt-btn rt-btn--tertiary rt-btn--sm" onClick={onReset}>
            <LuRotateCcw size={13} aria-hidden="true" /> Reset
          </button>
        )}
        <span className="pqd-filters__note">Times in UTC buckets</span>
      </div>
    </header>
  );
}
